import { memo, useState } from 'react'
import styled from 'styled-components'

import {
  keyProblemText,
  readableKeys,
  readKeys,
} from '../../model/imported-key'
import { PulseModal } from '../pulse-screen/pulse-modal'

import { shortAddress } from './money'

/** The colours a wallet can be given, the board's own rather than the app's. */
const COLOURS = [
  '#526fff',
  '#46b87d',
  '#e0a33c',
  '#e15c5c',
  '#9a6cf0',
  '#4ea7fa',
  '#d9709c',
  '#8a8c98',
] as const

/**
 * Making a wallet.
 *
 * One press, because there is nothing for a person to do here: the key is made
 * and held by us, so there is no phrase to write down and no password to
 * invent. That is the whole reason this is possible in a browser at all, and
 * it is what Axiom and GMGN do too.
 *
 * What we do not do is stay quiet about it. Custody is stated in the dialog,
 * beside the button, with the way out named — the key can be exported whenever
 * its owner wants it.
 */
export const CreateWalletDialog: React.FC<{
  onClose: () => void
  onCreate: (wallet: { name: string; colour: string }) => void
  busy: boolean
  error: string | undefined
  suggestedName: string
}> = memo(({ onClose, onCreate, busy, error, suggestedName }) => {
  const [name, setName] = useState(suggestedName)
  const [colour, setColour] = useState<string>(COLOURS[0])

  return (
    <PulseModal title="New wallet" onClose={onClose} width={440}>
      <Form>
        <label className="lbl" htmlFor="wallet-name">
          Name
        </label>
        <input
          id="wallet-name"
          className="fld"
          value={name}
          maxLength={32}
          placeholder={suggestedName}
          onChange={(event) => setName(event.target.value)}
        />

        <span className="lbl">Colour</span>
        <div className="swatches">
          {COLOURS.map((option) => (
            <button
              key={option}
              type="button"
              aria-label={`Colour ${option}`}
              aria-pressed={option === colour}
              className={option === colour ? 'sw on' : 'sw'}
              style={{ background: option }}
              onClick={() => setColour(option)}
            />
          ))}
        </div>

        <p className="note">
          We hold the key for this wallet, which is what lets it be made in a
          browser at all. You can export it whenever you want it — the wallet is
          yours, not an account with us.
        </p>

        {error ? <p className="bad">{error}</p> : undefined}

        <button
          className="go"
          type="button"
          disabled={busy}
          onClick={() =>
            onCreate({ name: name.trim() || suggestedName, colour })
          }
        >
          {busy ? 'Making it…' : 'Create wallet'}
        </button>
      </Form>
    </PulseModal>
  )
})

/**
 * Bringing wallets in from somewhere else.
 *
 * One key a line, as many as somebody has, because a person moving to us has
 * six of them and a dialog that takes one is the reason they stop after two.
 *
 * Each line is read in the browser before anything is sent, and the address it
 * controls is shown. That is the part worth having: Axiom takes the key and
 * tells you which wallet it was afterwards, so pasting the wrong line out of a
 * password manager is something you find out from the balance.
 */
export const ImportWalletsDialog: React.FC<{
  onClose: () => void
  onImport: (keys: ReadonlyArray<string>) => void
  busy: boolean
  error: string | undefined
  known: ReadonlySet<string>
}> = memo(({ onClose, onImport, busy, error, known }) => {
  const [text, setText] = useState('')
  const [shown, setShown] = useState(false)

  const lines = readKeys(text)
  const ready = readableKeys(lines).filter((line) => !known.has(line.address))
  const alreadyHere = readableKeys(lines).filter((line) =>
    known.has(line.address),
  )

  const paste = () => {
    navigator.clipboard
      .readText()
      .then((clip) =>
        setText((before) => (before ? `${before}\n${clip}` : clip)),
      )
      .catch(() => undefined)
  }

  return (
    <PulseModal title="Import wallets" onClose={onClose} width={520}>
      <Form>
        <div className="row">
          <label className="lbl" htmlFor="keys">
            Private keys, one a line
          </label>
          <button className="mini" type="button" onClick={paste}>
            Paste
          </button>
          <button
            className="mini"
            type="button"
            onClick={() => setShown(!shown)}
          >
            {shown ? 'Hide' : 'Show'}
          </button>
        </div>

        <textarea
          id="keys"
          className={shown ? 'fld area mono' : 'fld area mono masked'}
          rows={4}
          spellCheck={false}
          autoComplete="off"
          placeholder="Base58, a JSON array of 64 numbers, or hex"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />

        {lines.length > 0 ? (
          <ul className="read">
            {lines.map((line) => (
              <li key={line.index} className={line.reading.ok ? 'ok' : 'no'}>
                <b>{line.index + 1}</b>
                {line.reading.ok ? (
                  <>
                    <span className="mono">
                      {shortAddress(line.reading.address, 6)}
                    </span>
                    {known.has(line.reading.address) ? (
                      <i>already on this account</i>
                    ) : (
                      <i>will be imported</i>
                    )}
                  </>
                ) : (
                  <i>{keyProblemText[line.reading.reason]}</i>
                )}
              </li>
            ))}
          </ul>
        ) : undefined}

        <p className="note">
          A key pasted here is sent to us and held the same way a wallet we make
          is. Nobody should ever ask you for it — not support, not us anywhere
          but this box.
        </p>

        {error ? <p className="bad">{error}</p> : undefined}

        <button
          className="go"
          type="button"
          disabled={busy || ready.length === 0}
          onClick={() => onImport(ready.map((line) => line.raw))}
        >
          {importLabel({
            busy,
            ready: ready.length,
            alreadyHere: alreadyHere.length,
          })}
        </button>
      </Form>
    </PulseModal>
  )
})

/** What the import button says, which depends on three things at once. */
const importLabel = ({
  busy,
  ready,
  alreadyHere,
}: {
  busy: boolean
  ready: number
  alreadyHere: number
}) => {
  if (busy) {
    return 'Importing…'
  }
  if (ready > 0) {
    return `Import ${ready} ${ready === 1 ? 'wallet' : 'wallets'}`
  }
  return alreadyHere > 0 ? 'Already imported' : 'Import'
}

/**
 * Handing a key back.
 *
 * Hidden until asked for, never in the clipboard by accident, and with the one
 * sentence that matters: from here the wallet is only as safe as wherever this
 * ends up.
 */
export const ExportWalletDialog: React.FC<{
  onClose: () => void
  onReveal: () => void
  walletName: string
  secret: string | undefined
  busy: boolean
  error: string | undefined
}> = memo(({ onClose, onReveal, walletName, secret, busy, error }) => (
  <PulseModal title={`Export ${walletName}`} onClose={onClose} width={460}>
    <Form>
      <p className="note">
        The private key below is the wallet. Anyone who reads it can spend
        everything in it, and there is no undoing that — not by us, not by
        anyone.
      </p>

      {secret === undefined ? (
        <button
          className="go warn"
          type="button"
          disabled={busy}
          onClick={onReveal}
        >
          {busy ? 'Asking…' : 'Show the private key'}
        </button>
      ) : (
        <>
          <textarea
            className="fld area mono"
            rows={3}
            readOnly
            value={secret}
            onFocus={(event) => event.target.select()}
          />
          <button
            className="ghost"
            type="button"
            onClick={() =>
              navigator.clipboard.writeText(secret).catch(() => undefined)
            }
          >
            Copy it
          </button>
        </>
      )}

      {error ? <p className="bad">{error}</p> : undefined}
    </Form>
  </PulseModal>
))

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 4px 6px 6px;

  .row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .row .lbl {
    flex-grow: 1;
  }

  .lbl {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--t4);
  }

  .mini {
    height: 22px;
    padding: 0 8px;
    border-radius: 4px;
    border: 1px solid var(--chip-hi);
    background: var(--chip);
    color: var(--t3);
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
  }

  .mini:hover {
    color: var(--t1);
  }

  .fld {
    box-sizing: border-box;
    padding: 11px 12px;
    background: #0d0e12;
    border: 1px solid var(--line);
    border-radius: 5px;
    color: var(--t1);
    font-family: inherit;
    font-size: 14px;
    outline: none;
    resize: none;
  }

  .fld:focus {
    border-color: var(--accent);
  }

  .area {
    font-size: 12px;
    line-height: 18px;
  }

  .mono {
    font-family: 'Geist Mono', ui-monospace, monospace;
  }

  /*
   * A password field cannot hold several lines, so the hiding is done with the
   * text itself. It is a shoulder, not a secret store: the value is in the DOM
   * either way, which is true of every import box there is.
   */
  .masked {
    -webkit-text-security: disc;
    text-security: disc;
  }

  .swatches {
    display: flex;
    gap: 7px;
  }

  .sw {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    border: 2px solid transparent;
  }

  .sw.on {
    border-color: var(--t1);
  }

  .read {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 132px;
    overflow-y: auto;
    scrollbar-width: none;
  }

  .read::-webkit-scrollbar {
    display: none;
  }

  .read li {
    display: flex;
    align-items: center;
    gap: 9px;
    height: 28px;
    padding: 0 9px;
    border-radius: 5px;
    background: var(--head);
    border: 1px solid var(--line);
    font-size: 11.5px;
  }

  .read b {
    width: 14px;
    flex-shrink: 0;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 10px;
    color: var(--t5);
  }

  .read .mono {
    color: var(--t1);
    font-size: 11px;
  }

  .read i {
    font-style: normal;
    margin-left: auto;
    color: var(--t4);
    font-size: 11px;
  }

  .read li.no i {
    color: var(--down);
  }

  .note {
    margin: 0;
    font-size: 11.5px;
    line-height: 17px;
    color: var(--t4);
  }

  .bad {
    margin: 0;
    font-size: 12px;
    line-height: 18px;
    color: var(--down);
  }

  .go {
    height: 42px;
    border: 0;
    border-radius: 5px;
    background: var(--t1);
    color: var(--bg);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  .go:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .go.warn {
    background: var(--down);
    color: var(--t1);
  }

  .ghost {
    align-self: center;
    height: 30px;
    padding: 0 12px;
    border: 1px solid var(--chip-hi);
    border-radius: 5px;
    background: var(--chip);
    color: var(--t3);
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
  }
`
